// Manual CSV upload dialog. Editors only.
//
// The dialog intentionally stays thin: picks a file, hands it to the
// API client, surfaces the created/updated/skipped/error counts from
// the server. Error rows are shown collapsed — the admin can expand
// to see row numbers and reasons.
import React, { useState } from 'react';
import { Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { inventoryApi } from '../../api';

export default function InventoryImportDialog({ dealerId, onImported }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setBusy(false); setResult(null); };

  const onSubmit = async () => {
    if (!file) {
      toast.error('Pick a CSV file first.');
      return;
    }
    setBusy(true);
    try {
      const res = await inventoryApi.importCsv({ file, dealerId });
      setResult(res);
      toast.success(
        `Imported — ${res.created} created, ${res.updated} updated, ` +
        `${res.skipped} skipped, ${res.errors?.length || 0} error(s).`
      );
      onImported?.(res);
    } catch (err) {
      toast.error(err.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}
    >
      <DialogTrigger asChild>
        <Button size="sm" className="gap-2">
          <Upload className="w-4 h-4" /> Import CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manual inventory import</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            CSV columns accepted: <code>vin</code>, <code>stock_number</code>,
            <code> year</code>, <code>make</code>, <code>model</code>,
            <code> trim</code>, <code>category</code>, <code>condition</code>,
            <code> price</code>, <code>mileage</code>,
            <code> exterior_color</code>, <code>interior_color</code>,
            <code> url</code>, <code>images</code>,
            <code> availability</code>. Header aliases (e.g.
            <code> stock #</code>, <code>kms</code>, <code>link</code>) are
            accepted automatically.
          </p>
          <div>
            <Label>CSV file</Label>
            <Input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
          {result && (
            <div className="border rounded p-3 bg-muted/30">
              <div>Total rows: <b>{result.total_rows}</b></div>
              <div>Created: <b>{result.created}</b></div>
              <div>Updated: <b>{result.updated}</b></div>
              <div>Skipped: <b>{result.skipped}</b></div>
              <div>Errors: <b>{result.errors?.length || 0}</b></div>
              {result.errors?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs">
                    Show row-level issues
                  </summary>
                  <ul className="list-disc pl-5 text-xs text-red-700 mt-1">
                    {result.errors.slice(0, 50).map((e, idx) => (
                      <li key={idx}>
                        Row {e.row}
                        {e.field ? ` · ${e.field}` : ''}: {e.reason}
                      </li>
                    ))}
                    {result.errors.length > 50 && (
                      <li>… {result.errors.length - 50} more</li>
                    )}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          <Button disabled={busy || !file} onClick={onSubmit}>
            {busy ? 'Uploading…' : 'Import'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
